import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { initFilestackClient } from "network/filestack";
import { axiosService } from "network/axios";
import { updateProjectStatus, saveDraftProjectId } from "store/customerProject/reducer";
import { uploadProjectSelector, draftProjectIdSelector } from "store/customerProject/selector";
import { userSelector } from "store/user/selector";
import { calculateDueDate } from "network/helper";

const getLS_DRAFT_PROJECT_ID = (userId: string) => `order.draftProjectId.${userId}`;

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
  "application/json",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/tiff",
]);

const ALLOWED_EXTENSIONS = new Set(["pdf", "doc", "docx", "xls", "xlsx", "txt", "csv", "json", "png", "jpg", "jpeg", "tif", "tiff"]);

type ValidateResult = { ok: boolean; message: string | null };

export function useStep2Documents() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const rawUploadProject = useSelector(uploadProjectSelector as any) as any;
  const uploadProject = useMemo(() => rawUploadProject || {}, [rawUploadProject]);
  const user = useSelector(userSelector as any) as any;
  const draftProjectId = useSelector(draftProjectIdSelector as any) as string | null;
  const dispatch = useDispatch<any>();

  const [isUploading, setIsUploading] = useState(false);
  const isCreatingDraftRef = useRef(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<{ id: string; name: string; progress: number }[]>([]);

  const validateFiles = (files: File[]): ValidateResult => {
    if (files.length < 1) return { ok: false, message: null };

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return { ok: false, message: "Unsupported file type or size. Max 25 MB per file" };
      }

      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      const typeOk = file.type ? ALLOWED_MIME_TYPES.has(file.type) : true;
      const extOk = ext ? ALLOWED_EXTENSIONS.has(ext) : true;
      if (!typeOk || !extOk) {
        return { ok: false, message: "Unsupported file type or size. Max 25 MB per file" };
      }
    }

    return { ok: true, message: null };
  };

  const validatePickerFiles = (filesUploaded: any[]): ValidateResult => {
    if (!filesUploaded || filesUploaded.length < 1) {
      return { ok: false, message: null };
    }

    for (const f of filesUploaded) {
      const size = Number(f?.size ?? 0);
      if (size > MAX_FILE_SIZE_BYTES) {
        return { ok: false, message: "Unsupported file type or size. Max 25 MB per file" };
      }

      const fileName = String(f?.filename ?? "");
      const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
      const mimetype = String(f?.mimetype ?? "");
      const typeOk = mimetype ? ALLOWED_MIME_TYPES.has(mimetype) : true;
      const extOk = ext ? ALLOWED_EXTENSIONS.has(ext) : true;
      if (!typeOk || !extOk) {
        return { ok: false, message: "Unsupported file type or size. Max 25 MB per file" };
      }
    }

    return { ok: true, message: null };
  };

  // Create Draft project after first file upload
  const createDraftProject = useCallback(async (documents: any[]): Promise<string | null> => {
    if (!user?.user_id) return null;
    if (isCreatingDraftRef.current) return null;
    if (orderId) return orderId; // Already have an order from URL
    if (draftProjectId) return draftProjectId; // Already have a draft

    try {
      isCreatingDraftRef.current = true;

      const payload = {
        projectTitle: uploadProject.projectTitle,
        projectDescription:
          uploadProject.projectDescription || uploadProject.text || "",
        sourceLanguage: uploadProject.sourceLanguage || '', 
        targetLanguage: uploadProject.targetLanguage,
        transitionType: uploadProject.transitionType,
        documents: documents,
        document_cnt: documents?.length || 0,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        pageCount: 0,
        wordsCount: 0,
        price: 0,
        orderStatus: 10, // Draft status
        text: uploadProject.text || "",
        include_expedited_turnaround: false,
        isNotarized: false,
        isMailHardCopy: false,
        address: "",
        order_due_date: calculateDueDate(uploadProject),
      };

      const resp = await axiosService.post(`/projects/${user.user_id}`, payload);
      const newOrderId = (resp?.data as any)?.orderId;

      if (newOrderId) {
        localStorage.setItem(getLS_DRAFT_PROJECT_ID(user.user_id), newOrderId);
        dispatch(saveDraftProjectId(newOrderId));
        dispatch(updateProjectStatus({ orderId: newOrderId }));
        return newOrderId;
      }
      return null;
    } catch (e: any) {
      console.error("Failed to create draft project:", e);
      return null;
    } finally {
      isCreatingDraftRef.current = false;
    }
  }, [dispatch, draftProjectId, orderId, uploadProject, user]);

  // Update existing Draft project with new documents
  const updateDraftWithDocuments = useCallback(async (projectId: string, newDocuments: any[], allDocuments: any[]) => {
    if (!user?.user_id) return;

    try {
      const payload = {
        order_id: projectId,
        projectTitle: uploadProject.projectTitle || "",
        projectDescription:
          uploadProject.projectDescription || uploadProject.text || "",
        sourceLanguage: uploadProject.sourceLanguage || "English",
        targetLanguage: uploadProject.targetLanguage || [],
        transitionType: uploadProject.transitionType || "certified",
        documents: allDocuments,
        document_cnt: allDocuments?.length || 0,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        pageCount: uploadProject.totalPage || 0,
        wordsCount: uploadProject.totalWords || 0,
        price: 0,
        orderStatus: 10, // Keep as Draft
        text: uploadProject.text || "",
        include_expedited_turnaround: uploadProject.include_expedited_turnaround || false,
        isNotarized: uploadProject.isNotarized || false,
        isMailHardCopy: uploadProject.isMailHardCopy || false,
        address: uploadProject.address || "",
      };

      await axiosService.post(`/projects/${user.user_id}`, payload);
    } catch (e: any) {
      console.error("Failed to update draft with documents:", e);
    }
  }, [uploadProject, user]);

  const uploadPayloadToBackend = async (payload: any[]) => {
    const { data }: any = await axiosService.post(`/document/upload-cdn-documents`, { payload });
    const uploadResult = data?.result ?? [];

    const uploadedDocuments = uploadResult?.map((it: any) => {
      const { uploadId, pageCount, wordCount, parserSuccess, parserMessage } = it;
      const matchedFile = payload?.find((_it: any) => _it?.uploadId === uploadId);
      return {
        id: uploadId,
        pages: pageCount,
        words: wordCount,
        type: matchedFile?.type,
        fileUrl: matchedFile?.fileUrl || "",
        file: { name: matchedFile?.fileName },
        // Backend signals parserSuccess: false when it couldn't auto-detect
        // page/word counts. We carry it through Redux so OrderSummary can
        // show the manual-review disclaimer and useOrder's totals reducer
        // can skip the unreliable counts.
        parserSuccess: parserSuccess !== false,
        parserMessage: parserMessage ?? null,
        // Set later via onManualEntry when the customer provides an
        // estimate for a file the backend couldn't auto-count. Flag lives
        // alongside parserSuccess so totals reducers + UI badges can
        // distinguish auto-detected counts from customer-provided ones.
        parserManual: false,
      };
    });

    const existingDocs = (uploadProject?.documents as any[]) || [];
    const allDocuments = [...existingDocs, ...uploadedDocuments];
    
    dispatch(updateProjectStatus({ documents: allDocuments }));

    // Create or update Draft project after successful upload
    const currentProjectId = orderId || draftProjectId;
    if (!currentProjectId && allDocuments.length > 0) {
      // First upload - create Draft project
      await createDraftProject(allDocuments);
    } else if (currentProjectId) {
      // Subsequent uploads - update existing Draft
      await updateDraftWithDocuments(currentProjectId, uploadedDocuments, allDocuments);
    }
  };

  const uploadFiles = async (files: File[]) => {
    setIsUploading(true);
    setUploadError(null);

    const filestack = await initFilestackClient();

    const pending = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      progress: 0,
    }));
    setUploadingFiles(pending);

    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const localId = `${file.name}-${file.size}-${file.lastModified}`;
          const res: any = await filestack.upload(file, {
            onProgress: (event: any) => {
              const totalPercent = Number(event?.totalPercent);
              const total = Number(event?.total ?? 0);
              const loaded = Number(event?.loaded ?? 0);
              const next = Number.isFinite(totalPercent)
                ? Math.round(totalPercent)
                : total > 0
                  ? Math.round((loaded / total) * 100)
                  : 0;
              setUploadingFiles((prev) =>
                prev.map((it) =>
                  it.id === localId
                    ? { ...it, progress: Math.min(100, Math.max(0, next)) }
                    : it
                )
              );
            },
          });
          return {
            uploadId: res?.handle,
            fileUrl: res?.url,
            type: file.type,
            fileName: file.name,
          };
        })
      );

      setUploadingFiles((prev) => prev.map((it) => ({ ...it, progress: 100 })));
      await uploadPayloadToBackend(uploaded);
    } catch (e) {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadingFiles([]);
    }
  };

  const openPicker = async () => {
    if (isUploading) return;
    setUploadError(null);

    const filestack = await initFilestackClient();
    filestack
      .picker({
        maxFiles: 100,
        maxSize: MAX_FILE_SIZE_BYTES,
        accept: [
          "image/*",
          ".pdf",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".txt",
          ".csv",
          ".json",
          ".tif",
          ".tiff",
        ],
        onFileUploadStarted: (file: any) => {
          setIsUploading(true);
          setUploadError(null);
          const name = String(file?.filename ?? "");
          const size = Number(file?.size ?? 0);
          const id = `${name}-${size}`;
          setUploadingFiles((prev) => {
            if (prev.some((it) => it.id === id)) return prev;
            return [...prev, { id, name, progress: 0 }];
          });
        },
        onFileUploadProgress: (file: any, progress: any) => {
          const name = String(file?.filename ?? "");
          const size = Number(file?.size ?? 0);
          const id = `${name}-${size}`;
          const next = Math.round(Number(progress?.totalPercent ?? progress ?? 0));
          setUploadingFiles((prev) =>
            prev.map((it) => (it.id === id ? { ...it, progress: Math.min(100, Math.max(0, next)) } : it))
          );
        },
        onUploadDone: async (event: any) => {
          const { filesUploaded } = event;
          const { ok, message } = validatePickerFiles(filesUploaded);
          if (!ok) {
            setUploadError(message);
            return;
          }

          setUploadingFiles((prev) => prev.map((it) => ({ ...it, progress: 100 })));
          setIsUploading(true);
          try {
            const payload = (filesUploaded || []).map((it: any) => ({
              uploadId: it.uploadId,
              fileUrl: it.url,
              type: it.mimetype,
              fileName: it.filename,
            }));

            await uploadPayloadToBackend(payload);
          } catch (e) {
            setUploadError("Upload failed. Please try again.");
          } finally {
            setIsUploading(false);
            setUploadingFiles([]);
          }
        },
        onClose: () => {
          setIsUploading(false);
          setUploadingFiles([]);
        },
      })
      .open();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    disabled: isUploading,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted) => {
      const v = validateFiles(accepted);
      if (!v.ok) {
        setUploadError(v.message);
        return;
      }
      uploadFiles(accepted);
    },
    onDropRejected: () => {
      setUploadError("Unsupported file type or size. Max 25 MB per file");
    },
  });

  const totalDocs = ((uploadProject?.documents as any[]) || []).length || 0;

  const sameLanguageError = useMemo(() => {
    const source = String(uploadProject?.sourceLanguage || "").trim();
    const target = String(uploadProject?.targetLanguage?.[0] || "").trim();

    if (!source || !target) return null;

    if (source === target) return "Source and target languages must be different.";

    return null;
  }, [uploadProject?.sourceLanguage, uploadProject?.targetLanguage]);

  const languagePairError = useMemo(() => {
    const source = String(uploadProject?.sourceLanguage || "").trim();
    const target = String(uploadProject?.targetLanguage?.[0] || "").trim();

    if (!source || !target) return null;

    const hasEnglish = source === "English" || target === "English";
    if (!hasEnglish) return "One of the selected languages must be English.";

    return null;
  }, [uploadProject?.sourceLanguage, uploadProject?.targetLanguage]);

  // Block Continue while any document is in an unresolved parse-failure
  // state. Without this, a customer whose only file failed parsing could
  // checkout at $0 — the totals reducer correctly excludes the unparsed
  // file, but that means the cart is empty without telling the customer
  // why. Manual entry resolves to parserSuccess: true (with
  // parserManual: true), so it satisfies this check.
  const hasUnresolvedParseFailure = useMemo(() => {
    const docs = ((uploadProject?.documents as any[]) || []) as any[];
    return docs.some((d: any) => d?.parserSuccess === false);
  }, [uploadProject?.documents]);

  const canContinue =
    totalDocs > 0 &&
    !!uploadProject?.sourceLanguage &&
    (uploadProject?.targetLanguage?.length || 0) > 0 &&
    !sameLanguageError &&
    !languagePairError &&
    !isUploading &&
    !hasUnresolvedParseFailure;

  const onRemoveDocument = (documentId: string) => {
    const nextDocs = (((uploadProject?.documents as any[]) || []) as any[]).filter(
      (x: any) => String(x.id) !== String(documentId)
    );
    dispatch(updateProjectStatus({ documents: nextDocs }));
  };

  // Manual-entry handler. Customer typed an estimate into a failed
  // file's inline input. Flip parserSuccess true + tag as manual so the
  // OrderSummary banner stays up ("verified before charge"). Seeds
  // pages OR words depending on tier so the downstream calculator
  // (per-word for Standard, per-page for Certified) reads from the
  // right field; the inactive sibling stays 0 to avoid double-counting.
  const onManualEntry = useCallback(
    (documentId: string, value: number) => {
      const safeValue = Math.max(1, Math.round(Number(value) || 0));
      const isStandard = uploadProject?.transitionType === "standard";
      const docs = ((uploadProject?.documents as any[]) || []) as any[];
      const nextDocs = docs.map((d: any) =>
        String(d?.id) === String(documentId)
          ? {
              ...d,
              pages: isStandard ? 0 : safeValue,
              words: isStandard ? safeValue : 0,
              parserSuccess: true,
              parserManual: true,
              parserMessage: "manual_entry",
            }
          : d
      );
      dispatch(updateProjectStatus({ documents: nextDocs }));
    },
    [dispatch, uploadProject?.documents, uploadProject?.transitionType]
  );

  // "Get a free custom quote" deep-link. In-app customers go to the
  // existing /customer/support route — same destination the non-English
  // language-pair notice already uses, so customers see a consistent
  // escape hatch. No marketing-site round-trip needed; support team
  // sees the inbound and follows up with a quote.
  const onQuoteClick = useCallback(() => {
    navigate("/customer/support");
  }, [navigate]);

  const onSourceLanguageChange = (value: string) => {
    dispatch(updateProjectStatus({ sourceLanguage: value }));
  };

  const onTargetLanguageChange = (value: string) => {
    dispatch(updateProjectStatus({ targetLanguage: value ? [value] : [] }));
  };

  return {
    uploadProject,
    isUploading,
    uploadError,
    totalDocs,
    uploadingFiles,
    sameLanguageError,
    languagePairError,
    canContinue,
    hasUnresolvedParseFailure,
    getRootProps,
    getInputProps,
    isDragActive,
    openPicker,
    onRemoveDocument,
    onManualEntry,
    onQuoteClick,
    onSourceLanguageChange,
    onTargetLanguageChange,
  };
}
